import { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../context/Provider';
import AdditionalResource from '../AdditionalResource';
import ShareDesignModal from '../ShareDesignModal';
import TabbedContent from '../TabbedContent';
import Icon from '../Icon';
import { DEFAULT_FILTERS, LABELS, SOLUTIONS } from '../../config/data';
import { TYPE_OPEN_IN_NEW_TAB, TYPE_SHARE_ARROW } from '../../config/icon-types';
import {
	APPLICATION_PARAM,
	APP_ANCHOR,
	OPTIMIZATION_PARAM,
	SELECTED_TAB_PARAM,
	SENSOR_TYPE_PARAM,
	SOLUTION_ID_PARAM,
} from '../../config/app';
import { sendUserInteractionsToTelemetryFor } from '../../utility/analytics.telemetry';
import {
	MODIFY_DESIGN_CLICK_EVENT,
	SHARE_DESIGN_CLICK_EVENT,
} from '../../config/analytics.telemetry';
import './style.css';

/**
 * Renders solution content specific to the combination of selected filter dropdowns
 * @returns {React.ReactComponentElement} Sensor Solution component
 */
const Solution = () => {
	const classes = ['adi-isp__solution'];
	const { globalState } = useContext(AppContext);
	const { selectedFilters, selectedTab } = globalState;
	const { application, sensorType, optimization, solutionId } = selectedFilters;

	const selectedSolution =
		SOLUTIONS.find(solution => solution.solutionId === solutionId) ??
		// In case of invalid 'solutionId'
		SOLUTIONS.find(solution => solution.solutionId === DEFAULT_FILTERS.solutionId);

	const { title, modifyDesignUrl, additionalResources } = selectedSolution;
	const { shareDesignButton, modifyDesignButton } = LABELS;
	const [designUrl, setDesignUrl] = useState('');
	const [showShareDesignModal, setShowShareDesignModal] = useState(false);
	const shareDesignModalRef = useRef(null);

	/**
	 * Forms the design url from global state and returns it
	 * @returns {string} Url to the current selected solution design
	 */
	const getDesignUrl = () => {
		const url = new URL(window.location.href);

		url.search = ''; // Remove all unwanted or potentially invalid url params

		// Manually creating the url from selected values in global state to stay away
		// from potentially messed up params by user
		url.searchParams.set(APPLICATION_PARAM, application);
		url.searchParams.set(SENSOR_TYPE_PARAM, sensorType);
		url.searchParams.set(OPTIMIZATION_PARAM, optimization);
		url.searchParams.set(SOLUTION_ID_PARAM, solutionId);
		url.searchParams.set(SELECTED_TAB_PARAM, selectedTab);
		url.hash = APP_ANCHOR;

		return url.toString();
	};

	/**
	 * Opens the share design modal
	 */
	const openShareDesignModal = () => {
		const $shareModal = window.jQuery(shareDesignModalRef.current);

		$shareModal.modal(); // Open modal
		$shareModal.on('hidden.bs.modal', () => setShowShareDesignModal(false)); // Cleanup
	};

	/**
	 * Opens up a modal displaying a link to the currently selected solution to be copied to clipboard
	 */
	const onShareDesignClick = () => {
		const url = getDesignUrl();
		sendUserInteractionsToTelemetryFor(SHARE_DESIGN_CLICK_EVENT, { url }, solutionId);
		setDesignUrl(url);
		setShowShareDesignModal(true);
	};

	/**
	 * Makes a telemetry request for 'modify_design_click' event
	 */
	const onModifyDesignClick = () => {
		sendUserInteractionsToTelemetryFor(
			MODIFY_DESIGN_CLICK_EVENT,
			{ url: modifyDesignUrl },
			solutionId,
		);
	};

	/**
	 * Share and modify design buttons (extracted in a variable to prevent duplication)
	 */
	const solutionButtons = (
		<>
			<button onClick={onShareDesignClick} className={`${classes[0]}-share-design-button`}>
				<Icon className={`${classes[0]}-icon`} type={TYPE_SHARE_ARROW} />
				<span>{shareDesignButton}</span>
			</button>

			{modifyDesignUrl ? (
				<a
					onClick={onModifyDesignClick}
					href={modifyDesignUrl}
					target="_blank"
					rel="noopener noreferrer"
					className={`${classes[0]}-modify-design-link`}
				>
					<Icon className={`${classes[0]}-icon`} type={TYPE_OPEN_IN_NEW_TAB} />
					<span>{modifyDesignButton}</span>
				</a>
			) : null}
		</>
	);

	useEffect(() => {
		if (showShareDesignModal) openShareDesignModal();
	}, [showShareDesignModal]);

	return (
		<section className={classes.join(' ')}>
			<header className={`${classes[0]}-header`}>
				<h3 className={`${classes[0]}-title`}>{title}</h3>

				<div className={`${classes[0]}-buttons`}>{solutionButtons}</div>
			</header>

			<TabbedContent solution={selectedSolution} />

			<div className={`${classes[0]}-buttons ${classes[0]}-buttons--mobile`}>
				{solutionButtons}
			</div>

			<footer className={`${classes[0]}-additional-resources`}>
				{additionalResources.map((resource, index) => (
					<AdditionalResource
						key={`${resource.url}-${index}`}
						resource={resource}
						solutionId={solutionId}
					/>
				))}
			</footer>

			{showShareDesignModal ? (
				<ShareDesignModal ref={shareDesignModalRef} designUrl={designUrl} />
			) : null}
		</section>
	);
};

export default Solution;
